import { useCallback, useEffect, useRef, useState } from 'react'
import { MessageBubble } from '@/components/chat/MessageBubble'
import { ChatInput, type ChatInputHandle } from '@/components/chat/ChatInput'
import { ChatSidebar } from '@/components/chat/ChatSidebar'
import { useConversationsStore } from '@/stores/conversationsStore'
import { useAuthStore } from '@/stores/authStore'
import { assistantApi, api } from '@/lib/api'
import { Menu, Leaf, Sprout, Bug, TreePine, ChevronUp, MessageCircle, Wheat, WifiOff, RotateCcw } from 'lucide-react'
import { BrandIcon } from '@/components/ui/BrandIcon'
import { useOfflineQueue, type QueuedMessage } from '@/hooks/useOfflineQueue'

const GATEWAY_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

export function ChatScreen() {
  const { user } = useAuthStore()
  const {
    getCurrentConversation,
    addMessage,
    addMessageOptimistic,
    updateStreamingMessage,
    removeOptimisticMessage,
    commitMessage,
    createConversation,
    createLocalConversation,
    deleteConversation,
    loadConversations,
    setCurrentConversation,
    currentConversationId
  } = useConversationsStore()

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarWidth, setSidebarWidth] = useState(288) // 288px = w-72
  const [isLoading, setIsLoading] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false) // true após o primeiro token chegar
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [typingSeconds, setTypingSeconds] = useState(0)
  const [chatMode, setChatMode] = useState<'assistente' | 'agronomo'>('assistente')
  const [modelSettings, setModelSettings] = useState({
    chat_mode_assistente_model: 'gpt-4o-mini',
    chat_mode_agronomo_model: 'gpt-5.3-chat-latest',
  })
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<ChatInputHandle>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const stopGeneration = () => {
    abortControllerRef.current?.abort()
  }

  const handleRetryEdit = async (messageId: string, messageContent: string) => {
    if (!currentConversationId || isLoading) return
    // 1. Apaga do banco (melhor esforço — não bloqueia o UX)
    try {
      await assistantApi.delete(`/assistant/conversations/${currentConversationId}/messages/${messageId}`)
    } catch { /* swallow — mensagem some do store de qualquer forma */ }
    // 2. Remove do store local
    removeOptimisticMessage(currentConversationId, messageId)
    // 3. Manda o texto para o input (com foco e auto-resize)
    inputRef.current?.setValue(messageContent)
  }

  const onDrain = useCallback(async (item: QueuedMessage) => {
    // Create real conversation on server if this was queued offline as a new conversation
    const convId = (item.isNewConversation || !item.conversationId)
      ? await createConversation()
      : item.conversationId as string
    // Keep the conversation selected while the AI processes the queued message
    setCurrentConversation(convId)
    const response = await assistantApi.post('/assistant/chat', {
      message: item.message,
      conversation_id: convId,
      collection_name: item.collectionName,
      model: item.model,
      top_k: 30,
      max_context_tokens: 6000,
    })
    // Commit user message (removes pending state) and add bot response directly in the
    // store — no loadConversations here to avoid replacing the optimistic state prematurely
    await commitMessage(convId, item.tempId, item.displayText, true)
    const botText = response.data.response || 'Desculpe, não consegui responder.'
    await addMessage(convId, botText, false)
    setCurrentConversation(convId)
  }, [createConversation, commitMessage, addMessage, setCurrentConversation])

  const { isOnline, enqueue, pendingCount, isDraining } = useOfflineQueue(onDrain)

  // After drain finishes, sync conversation list (sidebar counts, titles, etc.)
  const prevIsDrainingRef = useRef(false)
  useEffect(() => {
    if (prevIsDrainingRef.current && !isDraining) {
      loadConversations()
    }
    prevIsDrainingRef.current = isDraining
  }, [isDraining, loadConversations])

  const currentConversation = getCurrentConversation()
  const messages = currentConversation?.messages || []

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Load conversations from backend on mount
  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  // Fetch chat model settings
  useEffect(() => {
    api.get('/auth/public/settings')
      .then(({ data }) => {
        setModelSettings({
          chat_mode_assistente_model: data.chat_mode_assistente_model || 'gpt-4o-mini',
          chat_mode_agronomo_model: data.chat_mode_agronomo_model || 'gpt-5.3-chat-latest',
        })
      })
      .catch(() => { })
  }, [])

  // Reset counter when drain starts
  useEffect(() => {
    if (isDraining) setTypingSeconds(0)
  }, [isDraining])

  // Timer for typing indicator
  useEffect(() => {
    if (!isTyping && !isDraining) return
    const interval = setInterval(() => setTypingSeconds(s => s + 1), 1000)
    return () => clearInterval(interval)
  }, [isTyping, isDraining])

  // Auto-focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const sendMessage = async (message: string, image?: File, audioBlob?: Blob, documents?: File[]) => {
    if (isLoading) return
    const hasMedia = !!(audioBlob || image || (documents && documents.length > 0))

    // Determine text shown in the user bubble
    let displayText = message
    if (audioBlob) {
      displayText = '🎤 Mensagem de voz'
    } else if (documents && documents.length > 0) {
      const docNamesEncoded = documents.map(d => d.name).join('|')
      const suffix = `\n\n__docs__:${docNamesEncoded}`
      displayText = message.trim() ? `${message.trim()}${suffix}` : suffix.trim()
    } else if (image) {
      displayText = message.trim() ? `📷 ${message.trim()}` : '📷 Imagem enviada'
    }

    const imagePreviewUrl: string | null = image ? URL.createObjectURL(image) : null

    // ── Offline queue (text only) ─────────────────────────────────────────────
    const offline = !navigator.onLine
    if (offline && !hasMedia) {
      if (currentConversationId) {
        const tempId = addMessageOptimistic(currentConversationId, displayText, true, undefined, true)
        enqueue({
          tempId,
          conversationId: currentConversationId,
          isNewConversation: false,
          message,
          displayText,
          collectionName: 'knowledge_base',
          model: chatMode === 'agronomo' ? modelSettings.chat_mode_agronomo_model : modelSettings.chat_mode_assistente_model,
        })
        setTimeout(() => inputRef.current?.focus(), 100)
        return
      }
      const localConvId = createLocalConversation()
      const tempId = addMessageOptimistic(localConvId, displayText, true, undefined, true)
      enqueue({
        tempId,
        conversationId: null,
        isNewConversation: true,
        message,
        displayText,
        collectionName: 'knowledge_base',
        model: chatMode === 'agronomo' ? modelSettings.chat_mode_agronomo_model : modelSettings.chat_mode_assistente_model,
      })
      setTimeout(() => inputRef.current?.focus(), 100)
      return
    }
    // ─────────────────────────────────────────────────────────────────────────

    setIsLoading(true)
    setIsTyping(true)
    setTypingSeconds(0)
    setTimeout(() => inputRef.current?.focus(), 100)

    // Novo AbortController para esta requisição
    abortControllerRef.current = new AbortController()

    let conversationId = currentConversationId
    let isNewConversation = false
    let pendingTempId: string | null = null      // bolha do usuário (optimistic)
    let streamingBotTempId: string | null = null  // bolha do bot em streaming

    try {
      if (!conversationId) {
        conversationId = await createConversation()
        setCurrentConversation(conversationId)
        isNewConversation = true
      }

      // Bolha optimistic do usuário
      pendingTempId = addMessageOptimistic(
        conversationId,
        displayText,
        true,
        imagePreviewUrl ?? undefined,
      )

      if (hasMedia) {
        // ── Mídia: axios + client-side abort ─────────────────────────────────
        const formData = new FormData()
        if (message.trim()) formData.append('message', message.trim())
        formData.append('conversation_id', conversationId)
        formData.append('collection_name', 'knowledge_base')
        formData.append('model', chatMode === 'agronomo' ? modelSettings.chat_mode_agronomo_model : modelSettings.chat_mode_assistente_model)
        formData.append('top_k', '30')
        formData.append('max_context_tokens', '6000')
        if (audioBlob) formData.append('audio', audioBlob, audioBlob instanceof File ? audioBlob.name : 'audio.webm')
        if (image) formData.append('image', image, image.name)
        if (documents) {
          for (const doc of documents) {
            formData.append('documents', doc, doc.name)
          }
        }

        const response = await assistantApi.post('/assistant/chat/media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
          signal: abortControllerRef.current.signal,
        })

        const userDbContent = audioBlob
          ? (response.data.metadata?.transcription || displayText)
          : displayText
        await commitMessage(conversationId, pendingTempId, userDbContent, true, imagePreviewUrl ?? undefined)
        pendingTempId = null

        const botResponse = response.data.response || 'Desculpe, não consegui responder.'
        await addMessage(conversationId, botResponse, false)

      } else {
        // ── Texto: fetch streaming (SSE) ──────────────────────────────────────
        const authToken = localStorage.getItem('auth_token')

        // Nota: NÃO criamos a bolha do bot aqui.
        // Ela só é criada quando o PRIMEIRO token chega, evitando bolha vazia
        // durante o pré-processamento (moderação + RAG, ~6-12s).

        const res = await fetch(`${GATEWAY_URL}/assistant/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({
            message,
            conversation_id: conversationId,
            collection_name: 'knowledge_base',
            model: chatMode === 'agronomo' ? modelSettings.chat_mode_agronomo_model : modelSettings.chat_mode_assistente_model,
            top_k: 30,
            max_context_tokens: 6000,
          }),
          signal: abortControllerRef.current.signal,
        })

        if (!res.ok) {
          throw Object.assign(new Error('Stream request failed'), { status: res.status })
        }

        // Consumir o stream SSE
        const reader = res.body!.getReader()
        const decoder = new TextDecoder()
        let accumulated = ''
        let sseBuffer = ''
        let streamError: string | null = null

        outer: while (true) {
          const { done, value } = await reader.read()
          if (done) break

          sseBuffer += decoder.decode(value, { stream: true })
          const lines = sseBuffer.split('\n')
          sseBuffer = lines.pop() ?? ''

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const raw = line.slice(6).trim()
            if (raw === '[DONE]') break outer
            try {
              const parsed = JSON.parse(raw)
              if (parsed.error) {
                streamError = parsed.error
                break outer
              }
              if (parsed.token) {
                if (!accumulated) {
                  // Primeiro token: cria a bolha agora + troca typing indicator pelo streaming
                  streamingBotTempId = addMessageOptimistic(conversationId!, '', false, undefined, false, true)
                  setIsTyping(false)
                  setIsStreaming(true)
                }
                accumulated += parsed.token
                if (streamingBotTempId) {
                  updateStreamingMessage(conversationId!, streamingBotTempId, accumulated)
                }
              }
            } catch {
              // chunk de SSE malformado — ignora
            }
          }
        }

        // Persiste a mensagem do usuário
        await commitMessage(conversationId, pendingTempId, displayText, true)
        pendingTempId = null

        const finalContent = streamError || accumulated || 'Desculpe, não consegui gerar uma resposta.'
        if (streamingBotTempId) {
          // Substitui a bolha de streaming pela mensagem final persistida no banco
          await commitMessage(conversationId, streamingBotTempId, finalContent, false)
          streamingBotTempId = null
        } else {
          // Nenhum token chegou (erro antes do streaming iniciar) — adiciona mensagem normalmente
          await addMessage(conversationId, finalContent, false)
        }
      }

    } catch (error: any) {
      // Abort pelo usuário (Stop button) — limpa silenciosamente sem mensagem de erro
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        if (pendingTempId && conversationId) {
          try { await commitMessage(conversationId, pendingTempId, displayText, true, imagePreviewUrl ?? undefined) } catch { /* swallow */ }
          pendingTempId = null
        }
        // Remove a bolha de streaming do store sem fazer DB write — resposta incompleta não é persistida
        if (streamingBotTempId && conversationId) {
          removeOptimisticMessage(conversationId, streamingBotTempId)
          streamingBotTempId = null
        }
        return
      }

      console.error('Chat error:', error)

      let errorMsg = "Desculpe, não foi possível processar sua mensagem no momento. O serviço pode estar temporariamente indisponível. Tente novamente em alguns instantes."
      const status = error?.status ?? error?.response?.status
      if (error?.code === 'ECONNABORTED' || error?.message?.toLowerCase?.().includes('timeout')) {
        errorMsg = "A resposta está demorando mais que o esperado. Tente uma pergunta mais objetiva ou aguarde alguns segundos e tente novamente."
      } else if (status === 429) {
        errorMsg = "Muitas mensagens em sequência. Aguarde um minuto e tente novamente."
      } else if (status === 502 || status === 503 || status === 504) {
        errorMsg = "O serviço está temporariamente indisponível (provavelmente em atualização). Tente novamente em instantes."
      } else if (status === 401) {
        errorMsg = "Sua sessão expirou. Recarregue a página e faça login novamente."
      } else if (status === 422) {
        errorMsg = "Sua mensagem ultrapassou o limite permitido. Por favor, reduza o texto para menos de 5.000 caracteres e tente novamente."
      } else if (status === 413) {
        errorMsg = "O arquivo enviado é muito grande. Limite: áudio 25 MB, documento 30 MB (até 5 docs, total 50 MB), imagem 10 MB."
      } else if (status === 415) {
        errorMsg = "Formato de arquivo não suportado. Formatos aceitos: áudio (webm, mp3, wav, ogg, m4a), imagem (jpeg, png, gif, webp), documento (pdf, docx, txt, md)."
      } else if (status === 403) {
        errorMsg = "Você não tem permissão para realizar essa ação."
      } else if (status === 500) {
        errorMsg = "Ocorreu um erro interno no serviço. Tente novamente em instantes."
      } else if (!error?.response && !error?.status) {
        errorMsg = "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente."
      }

      if (pendingTempId && conversationId) {
        try {
          await commitMessage(conversationId, pendingTempId, displayText, true, imagePreviewUrl ?? undefined)
          pendingTempId = null
        } catch { /* swallow */ }
      }

      if (conversationId) {
        try { await addMessage(conversationId, errorMsg, false) } catch { /* swallow */ }
      }

      if (isNewConversation && conversationId) {
        const conv = useConversationsStore.getState().conversations.find(c => c.id === conversationId)
        const hasPersistedMessage = conv?.messages.some(m => !m.id.startsWith('temp_'))
        if (!hasPersistedMessage) {
          try {
            await deleteConversation(conversationId)
            setCurrentConversation(null)
          } catch { /* swallow */ }
        }
      }

    } finally {
      abortControllerRef.current = null
      setIsLoading(false)
      setIsTyping(false)
      setIsStreaming(false)
      setTypingSeconds(0)
    }
  }

  const firstName = user?.name?.split(' ')[0] || ''

  const getPersonalizedSuggestions = () => {
    const base = [
      { text: "O que é agricultura regenerativa?", icon: Leaf },
      { text: "Como fazer compostagem?", icon: Sprout },
      { text: "Saúde do solo", icon: TreePine },
      { text: "Manejo biológico", icon: Bug },
    ]

    const profileSuggestions: { text: string; icon: typeof Leaf }[] = []

    if (user?.biome) {
      profileSuggestions.push({ text: `Práticas regenerativas para o bioma ${user.biome}`, icon: TreePine })
    }
    if (user?.soilType) {
      profileSuggestions.push({ text: `Manejo de solo ${user.soilType}`, icon: Sprout })
    }
    if (user?.challenges?.length) {
      profileSuggestions.push({ text: `Como lidar com ${user.challenges[0].toLowerCase()}?`, icon: Leaf })
    }
    if (user?.activities?.length) {
      profileSuggestions.push({ text: `Dicas para ${user.activities[0].toLowerCase()}`, icon: Sprout })
    }
    if (user?.mainObjective) {
      profileSuggestions.push({ text: `${user.mainObjective}`, icon: Leaf })
    }

    if (profileSuggestions.length >= 2) {
      return [...profileSuggestions.slice(0, 2), ...base.slice(0, 2)]
    }
    if (profileSuggestions.length === 1) {
      return [profileSuggestions[0], ...base.slice(0, 3)]
    }
    return base
  }

  const suggestions = getPersonalizedSuggestions()

  const getWelcomeSubtitle = () => {
    const parts: string[] = []
    if (user?.profileType === 'produtor' && user?.propertyCity) {
      parts.push(`Produtor em ${user.propertyCity}/${user.propertyState || ''}`)
    } else if (user?.profileType === 'profissional' && user?.workArea) {
      parts.push(`Profissional em ${user.workArea}`)
    } else if (user?.profileType === 'academico' && user?.institution) {
      parts.push(user.institution)
    }
    if (user?.preferredTopics?.length) {
      parts.push(`Interesses: ${user.preferredTopics.slice(0, 3).join(', ')}`)
    }
    if (parts.length > 0) {
      return parts.join(' · ')
    }
    return 'Pergunte-me sobre compostagem, saúde do solo, manejo biológico e muito mais!'
  }

  return (
    <div className="flex h-full">
      {/* Chat Sidebar - DESKTOP ONLY */}
      <div
        className="hidden lg:flex lg:flex-shrink-0"
        style={{ width: `${sidebarWidth}px` }}
      >
        <ChatSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onWidthChange={(width) => setSidebarWidth(width)}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-brand-overlay" onClick={() => setSidebarOpen(false)} />
          <div className="fixed left-0 top-0 bottom-0 w-72 bg-brand-surface">
            <ChatSidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full">
        {/* Chat Header - Desktop */}
        <div className="hidden lg:flex lg:flex-shrink-0 bg-brand-surface border-b border-brand-border px-4 py-3">
          <div className="flex items-center gap-3 w-full">
            <div className="w-9 h-9 bg-brand-accent rounded-xl flex items-center justify-center">
              <BrandIcon size={24} className="text-[#0A332F]" />
            </div>
            <div className="flex-1">
              <h1 className="text-base font-semibold text-brand-text">
                {currentConversation?.title || 'Assistente RegenerAI'}
              </h1>
              <p className="text-xs text-brand-text-muted">Agricultura Regenerativa</p>
            </div>
            {/* Chat Mode Toggle - Desktop */}
            <div className="flex items-center bg-brand-surface-alt rounded-xl p-1 gap-1">
              <button
                onClick={() => setChatMode('assistente')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${chatMode === 'assistente'
                  ? 'bg-brand-accent text-[#0A332F] shadow-sm'
                  : 'text-brand-text-secondary hover:text-brand-text'
                  }`}
              >
                <MessageCircle size={14} />
                Conversa
              </button>
              <button
                onClick={() => setChatMode('agronomo')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${chatMode === 'agronomo'
                  ? 'bg-brand-accent text-[#0A332F] shadow-sm'
                  : 'text-brand-text-secondary hover:text-brand-text'
                  }`}
              >
                <Wheat size={14} />
                Técnico
              </button>
            </div>
          </div>
        </div>

        {/* Chat Header - Mobile */}
        <div className="lg:hidden flex-shrink-0 bg-brand-surface border-b border-brand-border px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-xl hover:bg-brand-surface-alt text-brand-text-secondary transition-colors"
            >
              <Menu size={24} />
            </button>
            <div className="w-9 h-9 bg-brand-accent rounded-xl flex items-center justify-center">
              <BrandIcon size={24} className="text-[#0A332F]" />
            </div>
            <div className="flex-1">
              <h1 className="text-base font-semibold text-brand-text">
                {currentConversation?.title || 'Assistente RegenerAI'}
              </h1>
              <p className="text-xs text-brand-text-muted">Agricultura Regenerativa</p>
            </div>
          </div>
          {/* Chat Mode Toggle - Mobile */}
          <div className="flex items-center bg-brand-surface-alt rounded-xl p-1 gap-1 mt-2">
            <button
              onClick={() => setChatMode('assistente')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${chatMode === 'assistente'
                ? 'bg-brand-accent text-[#0A332F] shadow-sm'
                : 'text-brand-text-secondary hover:text-brand-text'
                }`}
            >
              <MessageCircle size={14} />
              Conversa
            </button>
            <button
              onClick={() => setChatMode('agronomo')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${chatMode === 'agronomo'
                ? 'bg-brand-accent text-[#0A332F] shadow-sm'
                : 'text-brand-text-secondary hover:text-brand-text'
                }`}
            >
              <Wheat size={14} />
              Técnico
            </button>
          </div>
        </div>

        {/* Offline Banner */}
        {!isOnline && (
          <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400">
            <WifiOff size={13} className="flex-shrink-0" />
            <span>
              {pendingCount > 0
                ? `Sem conexão — ${pendingCount} mensagem${pendingCount > 1 ? 's' : ''} aguardando envio`
                : 'Sem conexão — mensagens de texto serão enfileiradas e enviadas ao reconectar'}
            </span>
          </div>
        )}

        {/* Messages Container */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto min-h-0 bg-brand-bg scrollbar-thin pb-40 lg:pb-0"
          onScroll={e => setShowScrollTop((e.target as HTMLDivElement).scrollTop > 400)}
        >
          <div className="max-w-4xl mx-auto px-4 py-4 lg:py-6">
            {messages.length === 0 ? (
              <div className="text-center py-8 lg:py-16">
                <div className="w-16 h-16 bg-brand-primary-light rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <BrandIcon size={40} />
                </div>
                <h2 className="text-xl font-semibold text-brand-text mb-2">
                  {firstName ? `Olá, ${firstName}!` : 'Bem-vindo ao Assistente RegenerAI'}
                </h2>
                <p className="text-brand-text-secondary max-w-md mx-auto text-sm leading-relaxed">
                  {firstName
                    ? `Sou seu especialista em agricultura regenerativa. ${getWelcomeSubtitle()}`
                    : 'Sou seu especialista em agricultura regenerativa. Pergunte-me sobre compostagem, saúde do solo, manejo biológico e muito mais!'
                  }
                </p>

                {/* Quick Suggestions */}
                <div className="mt-6 lg:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto">
                  {suggestions.map(({ text, icon: Icon }) => (
                    <button
                      key={text}
                      onClick={() => sendMessage(text)}
                      className="flex items-center gap-3 px-4 py-3 card hover:border-brand-accent text-left text-sm text-brand-text-secondary hover:text-brand-text transition-all duration-200"
                    >
                      <Icon size={20} className="text-brand-accent flex-shrink-0" />
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <MessageBubble
                    key={`${message.id}-${index}`}
                    content={message.content}
                    isUser={message.isUser}
                    timestamp={message.timestamp}
                    userAvatar={message.isUser ? (user?.avatarUrl ?? null) : null}
                    imageUrl={message.imageUrl}
                    isPending={message.pending}
                    isStreaming={message.streaming}
                    messageId={message.id}
                    conversationId={currentConversationId ?? undefined}
                    feedback={message.feedback}
                  />
                ))}

                {/* Typing Indicator — só durante pré-processamento (antes do primeiro token) */}
                {(isTyping || isDraining) && !isStreaming && (
                  <MessageBubble
                    content=""
                    isUser={false}
                    isTyping={true}
                    typingSeconds={typingSeconds}
                  />
                )}

                {/* Retry button — aparece quando a última mensagem é do usuário sem resposta (geração cancelada) */}
                {(() => {
                  if (isLoading || isStreaming || messages.length === 0) return null
                  const last = messages[messages.length - 1]
                  const isRetryable =
                    last.isUser &&
                    !last.pending &&
                    !last.streaming &&
                    !last.imageUrl &&
                    !last.content.startsWith('🎤') &&
                    !last.content.includes('__docs__:')
                  if (!isRetryable) return null
                  return (
                    <div className="flex justify-end pr-1 -mt-1 mb-2 animate-fade-in">
                      <button
                        onClick={() => handleRetryEdit(last.id, last.content)}
                        className="flex items-center gap-1.5 text-xs text-brand-text-muted hover:text-brand-accent border border-brand-border hover:border-brand-accent rounded-full px-3 py-1.5 transition-all duration-200"
                      >
                        <RotateCcw size={12} />
                        Editar e reenviar
                      </button>
                    </div>
                  )
                })()}

                {/* Scroll Anchor */}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Scroll to top button */}
        {showScrollTop && messages.length > 0 && (
          <button
            onClick={() => messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-32 right-4 lg:bottom-24 lg:right-6 z-40 p-2.5 rounded-full bg-brand-surface border border-brand-border shadow-md text-brand-text-muted hover:text-brand-text hover:border-brand-accent transition-all duration-200 animate-fade-in"
            title="Voltar ao topo"
          >
            <ChevronUp size={18} />
          </button>
        )}

        {/* Chat Input */}
        <div className="lg:relative fixed bottom-20 left-0 right-0 lg:static lg:left-auto lg:right-auto lg:bottom-auto bg-brand-surface border-t border-brand-border z-50 lg:z-auto">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <ChatInput
              ref={inputRef}
              onSend={sendMessage}
              onStop={stopGeneration}
              isGenerating={isLoading}
              disabled={isDraining}
              placeholder="Digite sua pergunta..."
            />
          </div>
        </div>
      </div>
    </div>
  )
}